/**
 * Mission Memory — namespaced local-storage mirror.
 *
 * Locked rules (Patch 37.1):
 * - Mongo is the canonical source of truth. LS is a fast-hydration mirror.
 * - Key is namespaced by user + workspace + mission_type + mission_id so
 *   two users on the same device cannot see each other's data.
 * - Server always wins when server.version > local.version — the
 *   compare-and-swap logic lives in :func:`hydrateMissionFromServer`.
 * - Never persists secrets, provider data, tokens, raw prompts, or
 *   business records — only the mission's ``memory`` and lightweight
 *   progress/version markers.
 */
import type { Mission } from "./types";

const KEY_PREFIX = "orkexra:mission";

export type LocalMissionSnapshot = {
  id: string;
  workspace_id: string;
  mission_type: string;
  status: Mission["status"];
  stage: Mission["stage"];
  progress: Mission["progress"];
  memory: Mission["memory"];
  version: number;
  updated_at: string;
};

function keyOf(
  userId: string,
  workspaceId: string,
  missionType: string,
  missionId: string,
): string {
  return `${KEY_PREFIX}:${userId}:${workspaceId}:${missionType}:${missionId}`;
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

function isStorage(): Storage | null {
  try {
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage
      : null;
  } catch {
    return null;
  }
}

export function readLocalMission(
  userId: string,
  workspaceId: string,
  missionType: string,
  missionId: string,
): LocalMissionSnapshot | null {
  const storage = isStorage();
  if (!storage) return null;
  return safe(() => {
    const raw = storage.getItem(keyOf(userId, workspaceId, missionType, missionId));
    if (!raw) return null;
    return JSON.parse(raw) as LocalMissionSnapshot;
  }, null);
}

export function writeLocalMission(userId: string, mission: Mission): void {
  const storage = isStorage();
  if (!storage) return;
  safe(() => {
    const snapshot: LocalMissionSnapshot = {
      id: mission.id,
      workspace_id: mission.workspace_id,
      mission_type: mission.mission_type,
      status: mission.status,
      stage: mission.stage,
      progress: mission.progress,
      memory: mission.memory,
      version: mission.version,
      updated_at: mission.updated_at,
    };
    storage.setItem(
      keyOf(userId, mission.workspace_id, mission.mission_type, mission.id),
      JSON.stringify(snapshot),
    );
    return undefined;
  }, undefined);
}

export function clearLocalMission(
  userId: string,
  workspaceId: string,
  missionType: string,
  missionId: string,
): void {
  const storage = isStorage();
  if (!storage) return;
  safe(
    () => storage.removeItem(keyOf(userId, workspaceId, missionType, missionId)),
    undefined,
  );
}

/**
 * Reconcile a locally-cached snapshot against the server payload.
 * Returns the mission the caller should render.
 *
 * Server always wins when its ``version`` is greater or equal — the
 * local mirror is refreshed to match. If the server reports a
 * ``completed`` / ``cancelled`` state, the LS row is purged.
 */
export function reconcileMission(
  userId: string,
  server: Mission,
  local: LocalMissionSnapshot | null,
): Mission {
  if (server.status === "completed" || server.status === "cancelled") {
    clearLocalMission(
      userId, server.workspace_id, server.mission_type, server.id,
    );
    return server;
  }
  if (!local || server.version >= local.version) {
    writeLocalMission(userId, server);
    return server;
  }
  // Local newer than server — extraordinarily rare. Trust the server
  // (locked rule) but keep the LS mirror in sync via a write.
  writeLocalMission(userId, server);
  return server;
}
